import type { PiniaPluginContext, Store } from 'pinia';
import { watch } from 'vue';
import { debounce } from '@/core/utils/lodash';
import { guestLinkToClipboard } from '@/core/utils/clipboard';
import { camelToSnakeCase } from '@/core/utils/string.js';
import { getHash, setHash } from '@/core/utils/route';

class PiniaTPA {

	readonly #storeId: string;
	readonly #store: Store<string, any>;

	/**
	 * @deprecated
	 */
	readonly #Page: any;

	readonly #user: any;
	readonly #Api: Api.Client<Api.TV.Paths, true>;

	readonly #namesUrlHash: string[] = [];
	readonly #namesStorage: string[] = [];
	readonly #namesStorageLocal: string[] = [];
	readonly #namesGuestLink: string[] = [];

	constructor(context: Context) {
		this.#store = context.store;
		this.#storeId = this.#store.$id;

		this.#Page = context.options.Page;

		this.#user = context.options.user;
		this.#Api = context.options.Api;

		this.#namesUrlHash = context.options.tpaNamesUrlHash || [];
		this.#namesStorage = context.options.tpaNamesStorage || [];
		this.#namesStorageLocal = context.options.tpaNamesStorageLocal || [];
		this.#namesGuestLink = context.options.tpaNamesGuestLink || [];

		context.store.genGuestLink = <Path extends Api.TV.Path>(path: Path, params: Api.TV.Params<Path>) => this.genGuestLink(path, params);

		if (!this.#user.guest_data?.data) {
			this.#loadFromStorage();
			this.#loadFromStorage(true);

			// настройки URL hash имеют приоритет
			this.#loadFromHash();
		}

		// настройки гостевой ссылки имеют приоритет
		this.#loadFromGuestLink();

		this.#prepareParams();

		const save = debounce(() => {
			this.#saveInHash();
			this.#saveInStorage();
			this.#saveInStorage(true);
		});

		context.store.$subscribe(save);
	}

	/**
	 * Сгенерировать гостевую ссылку
	 *
	 * Метод, путь к которому указан в path, должен возвращать сгенерированную ссылку
	 */
	async genGuestLink<Path extends Api.TV.Path>(path: Path, params: Api.TV.Params<Path>) {
		const dataGuestLink = this.#genOptionsForGuestLink();
		params = Object.assign(params, Object.fromEntries(dataGuestLink));

		const res = await this.#Api.gen(path).setParams(params).call();
		if (res?.errors) return;

		const link = res.result as string;

		await guestLinkToClipboard(link);
	}

	/**
	 * Сгенерировать опции для гостевой ссылки
	 */
	#genOptionsForGuestLink() {
		const data = new Map();

		this.#namesGuestLink.forEach(name => {
			// формат параметров API: snake_case
			const nameSnakeCase = camelToSnakeCase(name);
			data.set(nameSnakeCase, this.#store[name]);
		});

		return data;
	}

	/**
	 * Установить опции из объекта без фиксации состояния
	 */
	#setOptions(data: Map<string, any>) {
		data.forEach((value, name) => {
			const currentValue = this.#store[name];

			if (value === null || value === undefined) return;
			if (currentValue === undefined) return;
			if (!value?.constructor) return;
			if (value.constructor !== currentValue.constructor) return;

			// дополнительная проверка на формат даты
			if (name.indexOf('date') === 0) {
				if (Array.isArray(value)) {
					// value.forEach((valueI) => {
					// 	if (!/\d\d\d\d-\d\d-\d\d/.test(valueI)) return;
					// });
				} else {
					if (typeof (value) === 'string') {
						if (!/\d\d\d\d-\d\d-\d\d/.test(value)) return;
					}
				}
			}

			this.#store[name] = value;
		});
	}

	/**
	 * Сгенерировать Map объект с опциями со значениями в виде json строк
	 */
	#genDataFormat(data: Map<string, any>) {
		const dataFormatted = new Map();

		data.forEach((value, name) => {
			if (value === null) return;

			value = JSON.stringify(value);

			dataFormatted.set(name, value);
		});

		return dataFormatted;
	}

	/**
	 * Сгенерировать Map объект с опциями со значениями в оригинальном виде
	 */
	#genDataUnFormat(dataFormatted: Map<string, string>) {
		const data: Map<string, any> = new Map();

		dataFormatted.forEach((value, name) => {
			if (value === null || value === undefined || value === 'false') return;

			try {
				if (typeof (value) === 'string' && !/^\d\d\d\d-\d\d-\d\d$/.test(value)) value = JSON.parse(value);

				if (value === null || value === undefined || value === 'false') return;
			} catch (e) {
				// не json строка
			}

			data.set(name, value);
		});

		return data;
	}

	/**
	 * Установить опции из хеша адреса страницы
	 */
	#loadFromHash() {
		let dataHash: any;
		try {
			dataHash = JSON.parse(getHash(this.#storeId));
		} catch (e) {

		}
		if (!dataHash) return;

		const data: Map<string, any> = new Map();

		this.#namesUrlHash.forEach((name) => {
			const value = dataHash[name];
			if (!value) return;

			data.set(name, value);
		});

		this.#setOptions(data);
	}

	/**
	 * Сохранить опции в хеш адреса страницы
	 */
	#saveInHash() {
		const data = new Map();

		this.#namesUrlHash.forEach((name) => {
			const value = this.#store[name];

			data.set(name, value);
		});

		const dataObject = Object.fromEntries(data);
		const dataJSON = JSON.stringify(dataObject);

		setHash(this.#storeId, dataJSON);
	};

	/**
	 * Установить опции из localStorage
	 * @param isLocal - сохранить с учетом адреса страницы
	 */
	#loadFromStorage(isLocal = false) {
		const dataFormatted = new Map();

		let names = this.#namesStorage;
		let storageNamespace = 'state:' + this.#storeId;

		if (isLocal) {
			names = this.#namesStorageLocal;
			storageNamespace = 'state:' + this.#storeId + ':' + location.pathname;
		}

		names.forEach((name) => {
			const value = localStorage.getItem(storageNamespace + ':' + name);
			dataFormatted.set(name, value);
		});

		const data = this.#genDataUnFormat(dataFormatted);

		this.#setOptions(data);
	}

	/**
	 * Сохранить опции в localStorage
	 * @param isLocal - сохранить с учетом адреса страницы
	 */
	#saveInStorage(isLocal = false) {
		const data = new Map();

		let names = this.#namesStorage;
		let storageNamespace = 'state:' + this.#storeId;

		if (isLocal) {
			names = this.#namesStorageLocal;
			storageNamespace = 'state:' + this.#storeId + ':' + location.pathname;
		}

		names.forEach((name) => {
			const value = this.#store[name];

			data.set(name, value);
		});

		const dataFormatted = this.#genDataFormat(data);

		names.forEach((name) => {
			const value = dataFormatted.get(name);

			localStorage.setItem(storageNamespace + ':' + name, value);
		});
	}

	/**
	 * Установить опции из гостевой ссылки
	 */
	#loadFromGuestLink() {
		const guestData = this.#user?.guest_data?.data;
		if (!guestData) return;

		// общие правила для гостевых сессий
		if (this.#user.id === -1) this.#user.positionsReverseDates = guestData.positionsReverseDates;

		const dataFormatted = new Map();

		this.#namesGuestLink.forEach((name) => {
			const value = guestData[name];
			dataFormatted.set(name, value);
		});

		const data = this.#genDataUnFormat(dataFormatted);

		this.#setOptions(data);

		const $guestTitle = $('.mod_guest_title');

		// TODO: вынести в опции плагина
		if (this.#store.competitorsIds?.length === 1 && this.#Page.page.data.competitors) {
			let competitors = this.#Page.page.data.competitors.filter((competitor: any) => competitor.id === guestData.competitorsIds[0]);
			if (competitors.length) {
				$('a', $guestTitle).attr('href', 'http://' + competitors[0].url);
				$('a', $guestTitle).text(competitors[0].name);
			}
		}
	}

	/**
	 * Подготовить входные параметры перед инициализацией основного функционала
	 *
	 * Входные параметры устанавливаются при открытии страницы
	 *
	 * Входные параметры могут меняться пользоваталем и устаревать
	 */
	#prepareParams() {
		// контролль за вводом регионов
		// TODO: вынести в опции плагина
		if (this.#store.regionsIndexes) {
			watch(this.#store.regionsIndexes, (regionsIndexes) => {
				if (!regionsIndexes.length) return;

				// приведение типа
				regionsIndexes.forEach((regionIndex: number, index: number) => regionsIndexes[index] = regionIndex);

				// оставить только включенные регионы
				// @ts-ignore
				const mapSearchers = TplProjectSelectorRegion.genSearchersMap();

				const availableRegionsIndexes: number[] = [];
				mapSearchers.forEach((searcher: any) => {
					searcher.regions.forEach((region: any) => {
						if (region.index === -1) return;

						availableRegionsIndexes.push(region.index);
					});
				});

				this.#store.regionsIndexes = regionsIndexes.filter((regionIndex: number) => availableRegionsIndexes.includes(regionIndex));
			}, { immediate: true });
		}

		// контролль за вводом конкурентов
		// TODO: вынести в опции плагина
		if (this.#store.competitorsIds) {
			watch(this.#store.competitorsIds, (competitorsIds) => {
				if (!competitorsIds.length) return;

				// приведение типа
				competitorsIds.forEach((competitorId: number, index: number) => competitorsIds[index] = competitorId);

				// оставить только включенных конкурентов
				const availableCompetitorsIds = [this.#Page.page.data.project.id];
				this.#Page.page.data.project.competitors.forEach((competitor: any) => {
					if (competitor.on >= 0) availableCompetitorsIds.push(competitor.id);
				});

				this.#store.competitorsIds = competitorsIds.filter((competitorId: number) => availableCompetitorsIds.includes(competitorId));
			}, { immediate: true });
		}
	}

}

type Options = {
	/**
	 * TODO: Убрать из плагина
	 */
	Page: any,

	/**
	 * Объект с настройками пользователя
	 */
	user: any,

	/**
	 * Объект для работы с API
	 *
	 * См. на сайте: @/component/api/core/api.ts
	 */
	Api: Api.Client<Api.TV.Paths, true>;

	/**
	 * Имена свойств для сохранения в URL
	 */
	tpaNamesUrlHash?: string[],

	/**
	 * Имена свойств для сохранения в localStorage
	 */
	tpaNamesStorage?: string[],

	/**
	 * Имена свойств для сохранения в localStorage с учетом пути URL
	 *
	 * Каждый URL будет работать со своим состоянием
	 */
	tpaNamesStorageLocal?: string[],

	/**
	 * Имена свойств для работы с гостевой ссылкой
	 *
	 * Для генерации гостевой ссылки используйте store.genGuestLink(data, url)
	 */
	tpaNamesGuestLink?: string[],
};

type Context = PiniaPluginContext & {
	options: Options
};

/**
 * Плагин tpa (Third-party access) для pinia
 *
 * Добавляет возможность сохранять и загружать данные в/из:
 * 	- URL hash в адресе страницы
 * 	- localStorage
 * 	- гостевая ссылка
 *
 * Для подключения плагина нужно указань одну или несколько опций при определении defineStore():
 * - tpaNamesUrlHash
 * - tpaNamesStorage
 * - tpaNamesStorageLocal
 * - tpaNamesGuestLink
 */
export default (contextPinia: PiniaPluginContext) => {
	const context = contextPinia as Context;

	// плагин подключать не нужно
	if (
		!context.options.tpaNamesUrlHash?.length &&
		!context.options.tpaNamesStorage?.length &&
		!context.options.tpaNamesStorageLocal?.length &&
		!context.options.tpaNamesGuestLink?.length
	) {
		return;
	}

	new PiniaTPA(context);
}
